import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _get from 'lodash/get';
import _every from 'lodash/every';
import _cloneDeep from 'lodash/cloneDeep';
import NewBookingDuck from 'ducks/new-booking-duck';
import NewBulkUploadDuck from 'ducks/new-bulk-upload-duck';
import parseFare from 'common/utils/parse-fare';
import { cumulateFare, displayCumulatedFare } from 'common/utils/cumulate-fare';
import {
  generateTimeSlotInDate,
  PickUpTimeSectionUI,
} from '../common-section-ui/pick-up-time-section-ui';
import useSelectDefaultDateTab from '../common-logic/use-default-tab';
import useChooseDefaultSlotInTab from '../common-logic/use-choose-default-slot-in-tab';

const pickUpNowCumulatedFareKey = '__NOW__';

const BulkPickUpTimeSection = ({
  dispatch,
  selectedServiceTypeKey,
  deliveryIDs,
  deliveries,
  countryCode,
}) => {
  const { allEmptySlot, selectedTimeSlot } = useMemo(
    () => {
      if (!deliveries || deliveries.length <= 0) {
        return undefined;
      }

      const slots = deliveries.map(delivery =>
        _get(delivery, 'detail.configuration.scheduledPickupSlot'),
      );

      const allEmptySlot = _every(slots, slot => slot === undefined);
      if (
        _every(
          slots,
          slot =>
            (!slots[0] && slot === slots[0]) ||
            (slots[0] &&
              slot &&
              slot.from === slots[0].from &&
              slot.to === slots[0].to),
        )
      ) {
        return { allEmptySlot, selectedTimeSlot: slots[0] };
      }

      return { allEmptySlot, selectedTimeSlot: undefined };
    },
    [deliveries],
  );

  const onTimeSlotClick = useCallback(
    timeSlot => () => {
      const scheduledPickupSlot =
        timeSlot && timeSlot.from && timeSlot.to ? timeSlot : null;
      dispatch(
        NewBookingDuck.creators.bulkUpdateServiceConfig(deliveryIDs, {
          scheduledPickupSlot,
        }),
      );
      dispatch(NewBulkUploadDuck.creators.trySetOfferConfig(null));
    },
    [dispatch, deliveryIDs],
  );

  const { pickUpNowFare, enablePickUpNow, timeSlots } = useMemo(
    () => {
      if (!deliveries || !selectedServiceTypeKey || deliveries.length <= 0) {
        return {};
      }

      let allPickUpNow = true;

      const timeSlotsAvailableCount = {};
      const cumulatedFares = {};
      const allTimeSlots = {};
      // generate timeSlot count map and total price
      deliveries.forEach(delivery => {
        // allPickUpNow?
        const enablePickUpNow = _get(
          delivery,
          'config.scheduleInfo.enablePickUpNow',
          false,
        );
        const pickUpNowFare = _get(
          delivery,
          'config.scheduleInfo.pickUpNowFare',
        );
        allPickUpNow = allPickUpNow && enablePickUpNow;
        if (allPickUpNow) {
          cumulateFare(
            cumulatedFares,
            pickUpNowCumulatedFareKey,
            parseFare(pickUpNowFare),
          );
        }

        const timeSlots = _get(delivery, 'config.scheduleInfo.timeSlots');
        if (!timeSlots) {
          return;
        }
        timeSlots.forEach(timeSlot => {
          const timeSlotKey = `${timeSlot.from}-${timeSlot.to}`;
          if (timeSlot.available) {
            timeSlotsAvailableCount[timeSlotKey] =
              _get(timeSlotsAvailableCount, timeSlotKey, 0) + 1;
            const parsedFare = parseFare(timeSlot.fare);
            cumulateFare(cumulatedFares, timeSlotKey, parsedFare);
            if (!allTimeSlots[timeSlotKey]) {
              allTimeSlots[timeSlotKey] = _cloneDeep(timeSlot);
            }
          }
        });
      });

      Object.keys(allTimeSlots).forEach(key => {
        if (timeSlotsAvailableCount[key] !== deliveries.length) {
          allTimeSlots[key].available = false;
        } else {
          allTimeSlots[key].fare = displayCumulatedFare(cumulatedFares[key]);
        }
      });

      return {
        pickUpNowFare: displayCumulatedFare(
          cumulatedFares[pickUpNowCumulatedFareKey],
        ),
        enablePickUpNow: allPickUpNow,
        timeSlots: Object.values(allTimeSlots),
      };
    },
    [selectedServiceTypeKey, deliveries],
  );

  const timeSlotsInTabs = useMemo(
    () => {
      const timeSlotsInTabs = {};
      if (!selectedServiceTypeKey) {
        return timeSlotsInTabs;
      }

      const selectedServiceType = _get(
        deliveries[0],
        'detail.configuration.serviceType',
      );
      const serviceTypes = _get(deliveries[0], 'config.serviceTypes', []);
      const serviceType = serviceTypes.find(
        st => st.id === selectedServiceType,
      );
      const pickUpSLA = _get(serviceType, 'metadata.pickUpSLA', 0);

      if (enablePickUpNow) {
        generateTimeSlotInDate(
          { available: true, fare: pickUpNowFare },
          pickUpSLA,
          false,
          countryCode,
          selectedTimeSlot,
          undefined,
          timeSlotsInTabs,
          onTimeSlotClick,
        );
      }
      timeSlots.forEach(timeSlot =>
        generateTimeSlotInDate(
          timeSlot,
          0,
          false,
          countryCode,
          selectedTimeSlot,
          undefined,
          timeSlotsInTabs,
          onTimeSlotClick,
        ),
      );

      return timeSlotsInTabs;
    },
    [dispatch, deliveryIDs, enablePickUpNow, pickUpNowFare, timeSlots],
  );

  const selectDefaultSlotInTab = useChooseDefaultSlotInTab(
    timeSlotsInTabs,
    onTimeSlotClick,
    undefined,
  );

  const [selectedTab, setSelectedTab] = useState(undefined);
  const onSelectTab = useCallback(
    tabKey => {
      setSelectedTab(tabKey);
    },
    [setSelectedTab],
  );
  useSelectDefaultDateTab(
    timeSlots,
    timeSlotsInTabs,
    selectedTab,
    selectedTimeSlot,
    setSelectedTab,
  );

  // only select default when all slot are empty
  useEffect(
    () => {
      if (selectedTimeSlot === undefined && allEmptySlot) {
        if (selectedTab) {
          selectDefaultSlotInTab(selectedTab);
        }
      }
    },
    [selectedTimeSlot, selectedTab, selectDefaultSlotInTab, allEmptySlot],
  );

  if (!timeSlots || timeSlots.length === 0) {
    return null;
  }

  return (
    <PickUpTimeSectionUI
      timeSlotsInTabs={timeSlotsInTabs}
      selectedTab={selectedTab}
      onSelectTab={onSelectTab}
    />
  );
};

BulkPickUpTimeSection.defaultProps = {
  selectedServiceTypeKey: undefined,
};

BulkPickUpTimeSection.propTypes = {
  dispatch: PropTypes.func.isRequired,
  selectedServiceTypeKey: PropTypes.string,
  countryCode: PropTypes.string.isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
};

export default BulkPickUpTimeSection;
